import type { CSSProperties } from 'react'

import { colors, componentTokens, shadows, typography } from '../KestrixDesignSystem.jsx'
import { colorTokens } from '../tokens/colors.ts'
import { TEXT_COLORS } from '../typography/colors'
import type { DropdownOptionSpacing } from './dropdown-option.tokens.ts'
import { dropdownOptionTokens } from './dropdown-option.tokens.ts'
import type { DropdownSize } from './types'

export const CONTROL_HEIGHT: Record<DropdownSize, string> = {
  small: componentTokens.input.height.xs,
  medium: componentTokens.input.height.md
}

export const FONT_SIZE: Record<DropdownSize, string> = {
  small: componentTokens.input.fontSize.xs,
  medium: componentTokens.input.fontSize.md
}

// ---------------------------------------------------------------------------
// Overlay tokens — shared visual values for any dropdown overlay
// (consumed by all KestrixDropdown variants so they stay visually in sync
// without duplicating magic numbers).
// ---------------------------------------------------------------------------

export const DROPDOWN_OVERLAY = {
  itemHoverBg: dropdownOptionTokens.colors.surfaceHover,
  itemSelectedBg: dropdownOptionTokens.colors.surfaceSelected,
  itemBorderRadius: dropdownOptionTokens.borderRadius,
  itemPadding: dropdownOptionTokens.spacing.cozy,
  itemSelectedFontWeight: 500,
  menuPadding: 6,
  menuGap: 6
} as const

// ---------------------------------------------------------------------------
// Dynamic style builders
// ---------------------------------------------------------------------------

type TriggerStyleParams = {
  height: string
  borderColor: string
  background: string
  cursor: string
}

export function getTriggerStyle({
  height,
  borderColor,
  background,
  cursor
}: TriggerStyleParams): CSSProperties {
  return {
    width: '100%',
    minHeight: height,
    borderRadius: componentTokens.input.borderRadius,
    border: `1px solid ${borderColor}`,
    background,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 10px',
    cursor,
    transition: 'border-color 150ms ease, background-color 150ms ease'
  }
}

type OptionStyleParams = {
  isSelected: boolean
  isHighlighted: boolean
  isDisabled: boolean
  fontSize: string
  spacing?: DropdownOptionSpacing
  isFocused?: boolean
  /** When false, selected rows use default chrome (checkbox indicates selection). */
  showSelectedChrome?: boolean
  tone?: 'danger'
}

export type OptionStyleResult = {
  style: CSSProperties
  textColor: string
}

type OptionTextColorParams = Pick<
  OptionStyleParams,
  'isSelected' | 'isDisabled'
> &
  Partial<
    Pick<OptionStyleParams, 'isHighlighted' | 'showSelectedChrome' | 'tone'>
  >

export function resolveOptionTextColor({
  isSelected,
  isHighlighted = false,
  isDisabled,
  showSelectedChrome = true,
  tone
}: OptionTextColorParams): string {
  const applySelectedChrome = showSelectedChrome && isSelected
  const isInteractive = isHighlighted && !isDisabled

  if (isDisabled) return dropdownOptionTokens.colors.textDisabled
  if (tone === 'danger') {
    return isInteractive
      ? colorTokens.semantic.status.errorSoftEmphasis.text
      : colorTokens.semantic.status.error
  }
  if (applySelectedChrome) return dropdownOptionTokens.colors.textSelected
  if (isInteractive) return dropdownOptionTokens.colors.textHover
  return dropdownOptionTokens.colors.textDefault
}

export function getOptionStyle({
  isSelected,
  isHighlighted,
  isDisabled,
  fontSize,
  spacing = 'cozy',
  isFocused = false,
  showSelectedChrome = true,
  tone
}: OptionStyleParams): OptionStyleResult {
  const isInteractive = isHighlighted && !isDisabled
  const applySelectedChrome =
    showSelectedChrome && isSelected && tone !== 'danger'
  const textColor = resolveOptionTextColor({
    isSelected,
    isHighlighted,
    isDisabled,
    showSelectedChrome,
    tone
  })
  const dangerHover = tone === 'danger' && isInteractive

  return {
    textColor,
    style: {
      width: '100%',
      border: applySelectedChrome
        ? `1px solid ${dropdownOptionTokens.colors.borderSelected}`
        : '1px solid transparent',
      boxSizing: 'border-box',
      textAlign: 'left',
      borderRadius: dropdownOptionTokens.borderRadius,
      padding: dropdownOptionTokens.spacing[spacing],
      cursor: isDisabled ? 'not-allowed' : 'pointer',
      fontSize,
      background: applySelectedChrome
        ? dropdownOptionTokens.colors.surfaceSelected
        : dangerHover
          ? colorTokens.semantic.status.errorSoftEmphasis.surface
          : isInteractive
            ? dropdownOptionTokens.colors.surfaceHover
            : 'transparent',
      color: textColor,
      fontWeight: applySelectedChrome
        ? DROPDOWN_OVERLAY.itemSelectedFontWeight
        : 400,
      outline: isFocused
        ? `${dropdownOptionTokens.focusOutlineWidth}px solid ${dropdownOptionTokens.colors.borderFocus}`
        : 'none',
      outlineOffset: dropdownOptionTokens.focusOutlineOffset
    }
  }
}

/** Options list chrome when the card is already provided by a Popover paper. */
export const embeddedOptionsMenuStyle: CSSProperties = {
  position: 'static',
  zIndex: 'auto',
  margin: 0,
  padding: 0,
  listStyle: 'none',
  border: 'none',
  background: 'transparent',
  boxShadow: 'none',
  maxHeight: 'none',
  overflow: 'visible',
  width: '100%'
}

export function getMenuStyle(height: string): CSSProperties {
  return {
    position: 'absolute',
    zIndex: 20,
    top: `calc(${height} + ${DROPDOWN_OVERLAY.menuGap}px)`,
    left: 0,
    right: 0,
    margin: 0,
    padding: DROPDOWN_OVERLAY.menuPadding,
    listStyle: 'none',
    borderRadius: componentTokens.input.borderRadius,
    border: `1px solid ${colors.border.light}`,
    background: colors.background.secondary,
    boxShadow: shadows.dropdown,
    maxHeight: 240,
    overflowY: 'auto'
  }
}

/** Same chrome as {@link getMenuStyle}, but fixed to a trigger rect for a body-portaled menu. */
export function getPortalMenuStyle(triggerRect: DOMRect): CSSProperties {
  return {
    position: 'fixed',
    zIndex: 1300,
    top: triggerRect.bottom + DROPDOWN_OVERLAY.menuGap,
    left: triggerRect.left,
    width: Math.max(triggerRect.width, 280),
    margin: 0,
    padding: DROPDOWN_OVERLAY.menuPadding,
    listStyle: 'none',
    borderRadius: componentTokens.input.borderRadius,
    border: `1px solid ${colors.border.light}`,
    background: colors.background.secondary,
    boxShadow: shadows.dropdown,
    maxHeight: 240,
    overflowY: 'auto'
  }
}

export function getInputStyle(searchQuery: string): CSSProperties {
  return {
    width: '100%',
    border: 'none',
    background: 'transparent',
    outline: 'none',
    color: searchQuery ? TEXT_COLORS['body-accent'] : TEXT_COLORS.disabled,
    fontFamily: typography.fontFamily.primary,
    fontSize: typography.body.small.fontSize,
    lineHeight: typography.body.small.lineHeight,
  }
}

// ---------------------------------------------------------------------------
// Derived value helpers
// ---------------------------------------------------------------------------

export function getBorderColor(error: boolean, isOpen: boolean): string {
  if (error) return colors.semantic.error.main
  if (isOpen) return colors.border.focus
  return colors.border.medium
}

export function getCursor(disabled: boolean, isSearch: boolean): string {
  if (disabled) return 'not-allowed'
  if (isSearch) return 'text'
  return 'pointer'
}

type FormDropdownChromeParams = {
  size?: DropdownSize
  error: boolean
  isOpen: boolean
  disabled: boolean
  isSearch?: boolean
  searchQuery?: string
}

export function getFormDropdownChrome({
  size = 'small',
  error,
  isOpen,
  disabled,
  isSearch = false,
  searchQuery = '',
}: FormDropdownChromeParams) {
  const height = CONTROL_HEIGHT[size]
  const fontSize = FONT_SIZE[size]
  const borderColor = getBorderColor(error, isOpen)
  const background = disabled
    ? colors.neutral[50]
    : colors.background.secondary
  const cursor = getCursor(disabled, isSearch)
  const triggerStyle = getTriggerStyle({
    height,
    borderColor,
    background,
    cursor,
  })
  const menuStyle = getMenuStyle(height)
  const inputStyle = getInputStyle(searchQuery)

  return { fontSize, triggerStyle, menuStyle, inputStyle }
}
